import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Loader2, ArrowLeft, Mail } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/hooks/useAuth";
import { getEdgeFunctionStatus, invokeEdgeFunction } from "@/lib/edge-functions";
import { getDeviceId, setDeviceId } from "@/lib/device-session";
import { getEdgeFunctionErrorMessage } from "@/lib/edge-function-errors";
import { DEFAULT_PASSWORD_POLICY, validatePassword } from "@/lib/password-policy";
import { logger } from "@/lib/logger";
import { FEATURE_FLAGS } from "@/lib/feature-flags";
import { useBiometricAuth } from "@/hooks/useBiometricAuth";
import { getAppUrl } from "@/lib/appUrl";
import {
  collectRolesFromMetadata,
  normalizeRoles,
  parseJwtPayload,
  PLATFORM_ROLES,
} from "@/hooks/accessRoleUtils";

// Import worship scene images
const authCathedral = "/images/auth-cathedral.jpg";
import authWelcome from "@/assets/auth-welcome.jpg";
import authPrayer from "@/assets/auth-prayer.jpg";
import authWorship from "@/assets/auth-worship.jpg";
import forgeLogo from "@/assets/forge-logo-stacked-on-dark.svg";

import {
  canHandleGiftLinkForAuthenticatedEmail,
  isPermanentGiftLinkFailure,
} from "./Auth.gift-link";

type AuthMode = "login" | "signup" | "forgot" | "resend";
type GiftLinkResult = "linked" | "access_required" | "pending" | "payment_failed" | "retryable_failed" | "failed" | "dormant";
type GiftLinkClaim = {
  organizationId?: string;
  contributionId?: string;
  checkoutSessionId?: string;
  donorName?: string;
  donorEmail?: string;
};

const GIFT_LINK_STORAGE_KEY = "publicGivingLink";
const ADMIN_ROUTE_PATTERN = /^\/admin(?:\/|$)/;
const PLATFORM_ADMIN_ROLES = [...PLATFORM_ROLES, "super_admin"] as const;

const splitName = (value: string) => {
  const trimmed = value.trim();
  if (!trimmed) return { firstName: "", lastName: "" };
  const parts = trimmed.split(/\s+/);
  if (parts.length === 1) return { firstName: parts[0], lastName: "" };
  return { firstName: parts[0], lastName: parts.slice(1).join(" ") };
};

const hasGiftReference = (claim: GiftLinkClaim) =>
  Boolean(claim.contributionId?.trim() || claim.checkoutSessionId?.trim());

const readStoredGiftLink = (): GiftLinkClaim | null => {
  if (typeof window === "undefined") return null;
  try {
    const stored = window.localStorage.getItem(GIFT_LINK_STORAGE_KEY);
    if (!stored) return null;
    const parsed = JSON.parse(stored) as unknown;
    if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) return null;
    return parsed as GiftLinkClaim;
  } catch {
    return null;
  }
};

const writeStoredGiftLink = (claim: GiftLinkClaim) => {
  if (typeof window === "undefined") return false;
  try {
    window.localStorage.setItem(GIFT_LINK_STORAGE_KEY, JSON.stringify(claim));
    return true;
  } catch {
    return false;
  }
};

const removeStoredGiftLink = () => {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.removeItem(GIFT_LINK_STORAGE_KEY);
  } catch {
    // The URL handoff remains available when browser storage is blocked.
  }
};

const hasStoredGiftLink = (authenticatedEmail?: string | null) => {
  const claim = readStoredGiftLink();
  return Boolean(
    claim?.organizationId?.trim()
    && hasGiftReference(claim)
    && canHandleGiftLinkForAuthenticatedEmail(claim.donorEmail, authenticatedEmail),
  );
};

const toRecord = (value: unknown): Record<string, unknown> | undefined => {
  if (typeof value === "object" && value !== null && !Array.isArray(value)) {
    return value as Record<string, unknown>;
  }
  return undefined;
};

const getRolesFromAccessToken = (accessToken?: string): string[] => {
  const payload = parseJwtPayload(accessToken);
  if (!payload) return [];

  const payloadRecord = toRecord(payload);
  const appMetadata = toRecord(payloadRecord?.app_metadata);

  // SECURITY: Only trust app_metadata for role decisions.
  // user_metadata is editable by the end user and MUST NOT be used
  // for authorization (Critical #9 remediation).
  return normalizeRoles([
    ...collectRolesFromMetadata(payloadRecord),
    ...collectRolesFromMetadata(appMetadata),
  ]);
};

const resolveLoginRedirect = async (
  redirectTarget: string,
  userId: string,
  accessToken?: string
): Promise<string> => {
  if (!ADMIN_ROUTE_PATTERN.test(redirectTarget)) {
    return redirectTarget;
  }

  try {
    const tokenRoles = getRolesFromAccessToken(accessToken);
    const { data, error } = await supabase
      .from("user_roles")
      .select("role")
      .eq("user_id", userId);

    if (error) {
      logger.warn("Could not verify admin-route access; falling back to home.", { ...error });
      return "/";
    }

    const dbRoles = normalizeRoles(
      (data ?? []).flatMap((row) =>
        typeof row.role === "string" ? [row.role] : []
      )
    );

    const resolvedRoles = new Set([...tokenRoles, ...dbRoles]);
    const canAccessAdminRoute = PLATFORM_ADMIN_ROLES.some((role) =>
      resolvedRoles.has(role)
    );

    return canAccessAdminRoute ? redirectTarget : "/";
  } catch (error) {
    logger.warn("Admin-route redirect check failed; falling back to home.", error);
    return "/";
  }
};

export default function Auth() {
  const appUrl = useMemo(() => getAppUrl(), []);
  const navigate = useNavigate();
  const location = useLocation();
  const [mode, setMode] = useState<AuthMode>("login");
  const [isLoading, setIsLoading] = useState(false);
  // Lazy-initialized from ?email=... so links that already know the address
  // (the account-recovery page's "Go to sign in", #4748 review; giftParams
  // below also writes into this same field once the org param is known)
  // land with it prefilled instead of making the user retype it.
  const [email, setEmail] = useState(() => new URLSearchParams(location.search).get("email") ?? "");
  const [password, setPassword] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [resetSent, setResetSent] = useState(false);
  const [resendSent, setResendSent] = useState(false);
  const [giftLinkPending, setGiftLinkPending] = useState(false);
  const [giftLinkRetryAttempt, setGiftLinkRetryAttempt] = useState(0);
  // A failed confirmation-code exchange can leave a different donor's
  // existing browser session intact. Keep the gift claim dormant until the
  // visitor explicitly signs in or signs up instead of applying it to that
  // retained session.
  const [giftLinkNeedsExplicitAuth, setGiftLinkNeedsExplicitAuth] = useState(true);
  const { toast } = useToast();
  const { isAuthenticated, isLoading: authCheckLoading, user } = useAuth();
  const { storeCredentials: storeBiometricCredentials } = useBiometricAuth();
  const giftLinkHandledRef = useRef(false);
  const confirmationCodeHandledRef = useRef<string | null>(null);
  const redirectTarget = useMemo(() => {
    const params = new URLSearchParams(location.search);
    const redirect = params.get("redirect");
    if (redirect && redirect.startsWith("/")) {
      if (!FEATURE_FLAGS.mfa && redirect.startsWith("/mfa/")) {
        return "/";
      }
      return redirect;
    }
    return "/";
  }, [location.search]);
  const giftParams = useMemo(() => {
    const params = new URLSearchParams(location.search);
    return {
      mode: params.get("mode"),
      organizationId: params.get("giftOrg"),
      contributionId: params.get("giftContribution"),
      checkoutSessionId: params.get("giftCheckoutSession"),
      donorName: params.get("giftName"),
      donorEmail: params.get("giftEmail"),
    };
  }, [location.search]);
  const currentGiftLinkDonorName = giftParams.donorName?.trim()
    || [firstName.trim(), lastName.trim()].filter(Boolean).join(" ");
  const authReason = useMemo(() => {
    const params = new URLSearchParams(location.search);
    return params.get("reason");
  }, [location.search]);
  const hasUrlGiftLink = Boolean(
    giftParams.organizationId && (giftParams.contributionId || giftParams.checkoutSessionId),
  );
  const hasGiftLink = hasUrlGiftLink || (isAuthenticated && hasStoredGiftLink(user?.email));
  const confirmationCode = useMemo(
    () => new URLSearchParams(location.search).get("code"),
    [location.search],
  );
  const giftConfirmationUrl = useMemo(() => {
    const confirmationUrl = new URL(typeof window === "undefined" ? appUrl : window.location.origin);
    confirmationUrl.pathname = "/auth";
    confirmationUrl.search = location.search;
    // A prior confirmation code is single-use. It must never be copied into a
    // resend redirect, where it would take precedence over the fresh code.
    confirmationUrl.searchParams.delete("code");
    // Browser storage is optional. Preserve the name collected during signup
    // in the confirmation link when the original gift had no name, so the
    // claim can still create the donor profile after email verification.
    if (
      giftParams.organizationId
      && (giftParams.contributionId || giftParams.checkoutSessionId)
      && !giftParams.donorName?.trim()
      && currentGiftLinkDonorName
    ) {
      confirmationUrl.searchParams.set("giftName", currentGiftLinkDonorName);
    }
    return confirmationUrl.toString();
  }, [appUrl, currentGiftLinkDonorName, giftParams, location.search]);

  // Redirect already-authenticated users away from the login page
  useEffect(() => {
    if (!authCheckLoading && isAuthenticated && !hasGiftLink) {
      navigate(redirectTarget, { replace: true });
    }
  }, [authCheckLoading, hasGiftLink, isAuthenticated, navigate, redirectTarget]);

  useEffect(() => {
    if (giftParams.mode && ["login", "signup", "forgot", "resend"].includes(giftParams.mode)) {
      setMode(giftParams.mode as AuthMode);
    }
    if (giftParams.donorEmail && !email) {
      setEmail(giftParams.donorEmail);
    }
    if (giftParams.donorName && (!firstName || !lastName)) {
      const { firstName: parsedFirstName, lastName: parsedLastName } = splitName(giftParams.donorName);
      if (!firstName) setFirstName(parsedFirstName);
      if (!lastName) setLastName(parsedLastName);
    }
    if (giftParams.organizationId && (giftParams.contributionId || giftParams.checkoutSessionId)) {
      writeStoredGiftLink({
        organizationId: giftParams.organizationId,
        contributionId: giftParams.contributionId,
        checkoutSessionId: giftParams.checkoutSessionId,
        donorName: currentGiftLinkDonorName,
        donorEmail: giftParams.donorEmail ?? "",
      });
    }
  }, [currentGiftLinkDonorName, email, firstName, giftParams, lastName]);

  // The web client disables detectSessionInUrl, so confirmation codes do not
  // establish a session automatically. Exchange the PKCE code on the same
  // host where signup stored its verifier, then the authenticated gift-link
  // effect below can finish the donor handoff.
  useEffect(() => {
    if (!confirmationCode || confirmationCodeHandledRef.current === confirmationCode) return;
    confirmationCodeHandledRef.current = confirmationCode;
    let cancelled = false;
    setIsLoading(true);
    void supabase.auth.exchangeCodeForSession(confirmationCode).then(({ error }) => {
      if (cancelled) return;
      if (error) {
        setGiftLinkNeedsExplicitAuth(true);
        const params = new URLSearchParams(location.search);
        params.delete("code");
        navigate(`${location.pathname}${params.size > 0 ? `?${params.toString()}` : ""}`, { replace: true });
        toast({
          title: "Confirmation link expired",
          description: "Please sign in to finish connecting your gift.",
          variant: "destructive",
        });
        setIsLoading(false);
        return;
      }
      setGiftLinkNeedsExplicitAuth(false);
      const params = new URLSearchParams(location.search);
      params.delete("code");
      navigate(`${location.pathname}${params.size > 0 ? `?${params.toString()}` : ""}`, { replace: true });
    });
    return () => {
      cancelled = true;
    };
  }, [confirmationCode, location.pathname, location.search, navigate, toast]);

  const linkGiftContribution = useCallback(async (authenticatedEmail?: string | null) => {
    const storedClaim = readStoredGiftLink();
    const urlClaim: GiftLinkClaim | null = giftParams.organizationId
      && (giftParams.contributionId || giftParams.checkoutSessionId)
      ? {
        organizationId: giftParams.organizationId,
        contributionId: giftParams.contributionId,
        checkoutSessionId: giftParams.checkoutSessionId,
        donorName: currentGiftLinkDonorName,
        donorEmail: giftParams.donorEmail ?? "",
      }
      : null;
    // The URL identifies the checkout return currently in progress. Prefer it
    // over a retained retry from an earlier gift in the same browser.
    const claim = urlClaim ?? storedClaim;
    if (!claim) return "linked" as const;

    if (!canHandleGiftLinkForAuthenticatedEmail(
      claim.donorEmail,
      authenticatedEmail ?? user?.email,
    )) {
      return "dormant" as const;
    }

    try {
      if (!claim.organizationId?.trim() || !hasGiftReference(claim)) {
        removeStoredGiftLink();
        return "linked" as const;
      }
      const { data, error } = await invokeEdgeFunction<{
        pending?: boolean;
        payment_failed?: boolean;
        has_giving_account_access?: boolean;
      }>("public-giving-link", {
        body: {
          organization_id: claim.organizationId,
          ...(claim.contributionId ? { contribution_id: claim.contributionId } : {}),
          ...(claim.checkoutSessionId ? { checkout_session_id: claim.checkoutSessionId } : {}),
          donor_name: claim.donorName?.trim() || currentGiftLinkDonorName || null,
          donor_email: claim.donorEmail ?? null,
        },
      });
      if (data?.pending) {
        return "pending" as const;
      }
      if (data?.payment_failed) {
        removeStoredGiftLink();
        return "payment_failed" as const;
      }
      if (!error) {
        removeStoredGiftLink();
        return data?.has_giving_account_access === false ? "access_required" as const : "linked" as const;
      }
      const status = getEdgeFunctionStatus(error);
      if (isPermanentGiftLinkFailure(status)) {
        removeStoredGiftLink();
        console.warn("Unable to link gift after login/signup.", error);
        return "failed" as const;
      }
      console.warn("Unable to link gift after login/signup.", error);
      return "retryable_failed" as const;
    } catch (error) {
      console.warn("Unable to link gift after login/signup.", error);
      return "retryable_failed" as const;
    }
  }, [currentGiftLinkDonorName, giftParams, user?.email]);

  const finishGiftLink = useCallback(async (authenticatedEmail?: string | null) => {
    if (giftLinkHandledRef.current) return;
    giftLinkHandledRef.current = true;
    const result: GiftLinkResult = await linkGiftContribution(authenticatedEmail);
    if (result === "pending" || result === "retryable_failed") {
      giftLinkHandledRef.current = false;
      setGiftLinkPending(true);
      setGiftLinkRetryAttempt((attempt) => attempt + 1);
      return;
    }
    setGiftLinkPending(false);
    setGiftLinkRetryAttempt(0);
    if (result === "dormant") {
      navigate(redirectTarget, { replace: true });
      return;
    }
    if (result === "payment_failed") {
      toast({
        title: "Payment didn't complete",
        description: "Stripe was unable to process this bank payment, so no gift was recorded.",
        variant: "destructive",
      });
      navigate(redirectTarget, { replace: true });
      return;
    }
    if (result === "failed") {
      toast({
        title: "Gift account setup needs attention",
        description: "Your account is ready, but we could not connect this gift. Please contact the organization for help.",
        variant: "destructive",
      });
      navigate(redirectTarget, { replace: true });
      return;
    }
    if (result === "access_required") {
      toast({
        title: "Gift linked to your profile",
        description: "Ask the organization to invite you before opening its member portal.",
      });
      navigate(redirectTarget, { replace: true });
      return;
    }
    navigate(redirectTarget, { replace: true });
  }, [linkGiftContribution, navigate, redirectTarget, toast]);

  useEffect(() => {
    if (!giftLinkPending) return;
    const retry = window.setTimeout(() => {
      void finishGiftLink();
    }, 30_000);
    return () => window.clearTimeout(retry);
  }, [finishGiftLink, giftLinkPending, giftLinkRetryAttempt]);

  // Email-confirmed signups return with a session on the Auth route. Keep the
  // gift parameters in that return URL and finish the authenticated claim
  // before routing the donor to the member giving page.
  useEffect(() => {
    // A browser may already hold another Forge session when it opens a
    // confirmation link. Do not claim the gift with that prior token while
    // the PKCE code exchange is still replacing it.
    if (
      confirmationCode
      || authCheckLoading
      || !isAuthenticated
      || !hasGiftLink
      || giftLinkNeedsExplicitAuth
    ) return;
    void finishGiftLink();
  }, [
    authCheckLoading,
    confirmationCode,
    finishGiftLink,
    giftLinkNeedsExplicitAuth,
    hasGiftLink,
    isAuthenticated,
  ]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      if (mode === "login") {
        const deviceId = getDeviceId();
        const { data, error } = await invokeEdgeFunction<{
          access_token: string;
          refresh_token: string;
          user: { id: string };
          device_id?: string;
        }>("auth-login", {
          body: {
            email,
            password,
            device_id: deviceId,
            platform: "web",
          },
          preserveSessionOnAuthError: true,
        });

        if (error) throw error;
        if (!data?.access_token || !data?.refresh_token || !data?.user?.id) {
          throw new Error("Unable to start session. Please try again.");
        }

        const { data: sessionData, error: sessionError } = await supabase.auth.setSession({
          access_token: data.access_token,
          refresh_token: data.refresh_token,
        });
        if (sessionError) throw sessionError;
        if (!sessionData.session || !sessionData.user) {
          throw new Error("Unable to start session. Please try again.");
        }

        if (data.device_id) {
          setDeviceId(data.device_id);
        } else if (deviceId) {
          setDeviceId(deviceId);
        }

        const accessToken = sessionData.session.access_token;
        const refreshToken = sessionData.session.refresh_token;
        const userId = sessionData.user.id;

        // Store credentials for biometric unlock (native only, silent failure)
        storeBiometricCredentials(email, refreshToken).catch(() => {});

        if (hasUrlGiftLink || hasStoredGiftLink(sessionData.user.email)) {
          await finishGiftLink(sessionData.user.email);
        } else {
          const safeRedirectTarget = await resolveLoginRedirect(redirectTarget, userId, accessToken);
          navigate(safeRedirectTarget);
        }
      } else if (mode === "signup") {
        // Public signup has no org context yet — enforce the platform password
        // baseline (W6-4) before creating the account.
        const passwordCheck = validatePassword(password, DEFAULT_PASSWORD_POLICY);
        if (!passwordCheck.valid) {
          toast({
            title: "Password doesn't meet requirements",
            description: passwordCheck.errors.join(" "),
            variant: "destructive",
          });
          return;
        }
        const { data, error } = await supabase.auth.signUp({
          email,
          password,
          options: {
            emailRedirectTo: giftConfirmationUrl,
            data: {
              first_name: firstName,
              last_name: lastName,
            },
          },
        });
        if (error) throw error;

        if (!data.session) {
          toast({
            title: "Check your email",
            description: "We sent you a confirmation link to finish creating your account.",
          });
          return;
        }

        if (hasUrlGiftLink || hasStoredGiftLink(data.user?.email)) {
          await finishGiftLink(data.user?.email);
        } else {
          navigate(redirectTarget);
        }
      } else if (mode === "forgot") {
        const { error } = await supabase.auth.resetPasswordForEmail(email, {
          redirectTo: `${appUrl}/reset-password`,
        });
        if (error) throw error;
        
        setResetSent(true);
        toast({
          title: "Check your email",
          description: "We sent you a password reset link.",
        });
      } else if (mode === "resend") {
        const { error } = await supabase.auth.resend({
          type: "signup",
          email,
          options: {
            emailRedirectTo: giftConfirmationUrl,
          },
        });
        if (error) throw error;
        
        setResendSent(true);
        toast({
          title: "Verification email sent",
          description: "Check your inbox for the confirmation link.",
        });
      }
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
      logger.error("Auth submit failed", { mode, message: error?.message });
    } finally {
      setIsLoading(false);
    }
  };

  const switchMode = (newMode: AuthMode) => {
    setMode(newMode);
    setResetSent(false);
    setResendSent(false);
  };

  const getTitle = () => {
    switch (mode) {
      case "forgot": return "Reset Password";
      case "resend": return "Resend Verification";
      default: return "Forge";
    }
  };

  const getSubtitle = () => {
    switch (mode) {
      case "login": return "Sign in to continue your mission";
      case "signup": return "Join us in serving your community";
      case "forgot": return "Enter your email to receive a reset link";
      case "resend": return "Enter your email to resend the confirmation link";
    }
  };

  return (
    <main
      aria-label={getTitle()}
      tabIndex={-1}
      className="min-h-screen relative overflow-hidden bg-slate-950"
    >
      {/* Four Corner Images with refined gradients */}
      
      {/* Top Left - Gothic Cathedral */}
      <div className="absolute top-0 left-0 w-1/2 h-1/2 overflow-hidden">
        <img 
          src={authCathedral} 
          alt="" 
          className="w-full h-full object-cover"
          loading="eager"
        />
        <div className="absolute inset-0 bg-gradient-to-br from-slate-950/40 via-slate-950/80 to-slate-950" />
      </div>

      {/* Top Right - Church Welcome */}
      <div className="absolute top-0 right-0 w-1/2 h-1/2 overflow-hidden">
        <img 
          src={authWelcome} 
          alt="" 
          className="w-full h-full object-cover"
          loading="eager"
        />
        <div className="absolute inset-0 bg-gradient-to-bl from-slate-950/40 via-slate-950/80 to-slate-950" />
      </div>

      {/* Bottom Left - Prayer & Healing */}
      <div className="absolute bottom-0 left-0 w-1/2 h-1/2 overflow-hidden">
        <img 
          src={authPrayer} 
          alt="" 
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-tr from-slate-950/40 via-slate-950/80 to-slate-950" />
      </div>

      {/* Bottom Right - Modern Worship */}
      <div className="absolute bottom-0 right-0 w-1/2 h-1/2 overflow-hidden">
        <img 
          src={authWorship} 
          alt="" 
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-tl from-slate-950/40 via-slate-950/80 to-slate-950" />
      </div>

      {/* Center vignette for form focus */}
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,_transparent_0%,_transparent_20%,_rgb(2_6_23_/_0.7)_50%,_rgb(2_6_23)_70%)]" />

      {/* Atmospheric glow behind logo area */}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] pointer-events-none">
        <div className="absolute inset-0 bg-gradient-radial from-amber-500/[0.14] via-amber-500/[0.04] to-transparent rounded-full blur-3xl animate-pulse" style={{ animationDuration: '4s' }} />
        <div className="absolute inset-8 bg-gradient-radial from-white/[0.07] via-white/[0.02] to-transparent rounded-full blur-2xl" />
      </div>

      {/* Main Content */}
      <div className="relative z-10 min-h-screen flex items-center justify-center px-4 pt-[calc(var(--safe-area-inset-top)+1rem)] pb-[calc(var(--safe-area-inset-bottom)+1rem)]">
        <div className="w-full max-w-md space-y-8">
          {/* Logo and Branding */}
          <div className="text-center relative">
            {/* Warm ambient glow behind logo */}
            <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
              <div className="w-72 h-72 bg-gradient-radial from-amber-500/[0.18] via-amber-500/[0.06] to-transparent rounded-full blur-3xl" />
            </div>

            {/* Forge mark and lettering with a warm metallic glow */}
            <div className="relative mx-auto group w-40 sm:w-[200px]">
              <img
                src={forgeLogo}
                alt="Forge"
                width={439}
                height={390}
                className="w-full h-auto transition-all duration-700 group-hover:scale-[1.03]"
                style={{ filter: 'drop-shadow(0 0 40px rgba(245, 158, 11, 0.3)) drop-shadow(0 0 80px rgba(245, 158, 11, 0.15))' }}
              />
            </div>

            <p className="mt-4 text-base text-slate-300 tracking-wide">
              {getSubtitle()}
            </p>
          </div>

          {/* Form Card */}
          <div className="rounded-2xl border border-white/10 bg-slate-900 p-8 shadow-2xl">
            {authReason === "session-expired" && (
              <Alert className="mb-6 border-white/10 bg-white/5 text-slate-100">
                <AlertTitle>Session expired</AlertTitle>
                <AlertDescription>
                  Please sign in again to continue where you left off.
                </AlertDescription>
              </Alert>
            )}
            {authReason === "gift" && (
              <Alert className="mb-6 border-white/10 bg-white/5 text-slate-100">
                <AlertTitle>Link your gift</AlertTitle>
                <AlertDescription>
                  Sign in or create an account to connect your recent contribution to your donor profile.
                </AlertDescription>
              </Alert>
            )}
            {giftLinkPending && (
              <Alert className="mb-6 border-white/10 bg-white/5 text-slate-100">
                <AlertTitle>Your bank payment is processing</AlertTitle>
                <AlertDescription>
                  Keep this page open. We’ll connect the gift to your account automatically after Stripe confirms it.
                </AlertDescription>
              </Alert>
            )}
            {/* Reset password sent confirmation */}
            {mode === "forgot" && resetSent ? (
              <div className="text-center space-y-4">
                <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-primary/10 ring-2 ring-primary/40">
                  <Mail className="h-7 w-7 text-primary" />
                </div>
                <p className="text-sm text-slate-300">
                  If an account exists for <strong className="text-white">{email}</strong>, you'll receive a password reset link shortly.
                </p>
                <Button
                  variant="outline"
                  onClick={() => switchMode("login")}
                  className="w-full bg-slate-800 border-slate-700 text-white hover:bg-slate-700"
                >
                  <ArrowLeft className="mr-2 h-4 w-4" />
                  Back to Sign In
                </Button>
              </div>
            ) : mode === "resend" && resendSent ? (
              /* Resend verification sent confirmation */
              <div className="text-center space-y-4">
                <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-primary/10 ring-2 ring-primary/40">
                  <Mail className="h-7 w-7 text-primary" />
                </div>
                <p className="text-sm text-slate-300">
                  We've sent a new verification email to <strong className="text-white">{email}</strong>. Check your inbox and spam folder.
                </p>
                <Button
                  variant="outline"
                  onClick={() => switchMode("login")}
                  className="w-full bg-slate-800 border-slate-700 text-white hover:bg-slate-700"
                >
                  <ArrowLeft className="mr-2 h-4 w-4" />
                  Back to Sign In
                </Button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-5">
                {mode === "signup" && (
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="firstName" className="text-slate-200 font-medium">First name</Label>
                      <Input
                        id="firstName"
                        name="given-name"
                        autoComplete="given-name"
                        autoCapitalize="words"
                        enterKeyHint="next"
                        value={firstName}
                        onChange={(e) => setFirstName(e.target.value)}
                        required={mode === "signup"}
                        placeholder="John"
                        className="bg-slate-800 border-slate-600 text-white placeholder:text-slate-500 focus:border-primary focus:ring-primary"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="lastName" className="text-slate-200 font-medium">Last name</Label>
                      <Input
                        id="lastName"
                        name="family-name"
                        autoComplete="family-name"
                        autoCapitalize="words"
                        enterKeyHint="next"
                        value={lastName}
                        onChange={(e) => setLastName(e.target.value)}
                        required={mode === "signup"}
                        placeholder="Doe"
                        className="bg-slate-800 border-slate-600 text-white placeholder:text-slate-500 focus:border-primary focus:ring-primary"
                      />
                    </div>
                  </div>
                )}

                <div className="space-y-2">
                  <Label htmlFor="email" className="text-slate-200 font-medium">Email</Label>
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      // Login pairs the email with a saved password, so advertise
                      // it as the credential "username" — that's the token iOS
                      // Password AutoFill and password managers key off of. For
                      // sign-up / reset flows there's no paired password, so plain
                      // "email" is the more accurate hint.
                      autoComplete={mode === "login" ? "username" : "email"}
                      inputMode="email"
                      autoCapitalize="none"
                      autoCorrect="off"
                      spellCheck={false}
                      enterKeyHint={mode === "login" ? "next" : "go"}
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      required
                      placeholder="you@example.com"
                    className="bg-slate-800 border-slate-600 text-white placeholder:text-slate-500 focus:border-primary focus:ring-primary"
                  />
                </div>

                {mode !== "forgot" && mode !== "resend" && (
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <Label htmlFor="password" className="text-slate-200 font-medium">Password</Label>
                      {mode === "login" && (
                        <button
                          type="button"
                          onClick={() => switchMode("forgot")}
                          className="text-sm text-primary hover:text-primary/80 transition-colors font-medium"
                        >
                          Forgot password?
                        </button>
                      )}
                    </div>
                    <Input
                      id="password"
                      name="password"
                      type="password"
                      // Signing up should offer iOS/Keychain strong-password
                      // generation ("new-password"); signing in should offer the
                      // saved credential ("current-password").
                      autoComplete={mode === "signup" ? "new-password" : "current-password"}
                      enterKeyHint={mode === "login" ? "go" : "next"}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      required
                      minLength={6}
                      placeholder="••••••••"
                      className="bg-slate-800 border-slate-600 text-white placeholder:text-slate-500 focus:border-primary focus:ring-primary"
                    />
                  </div>
                )}

                {mode === "signup" && (
                  <div className="rounded-lg border border-slate-700/60 bg-slate-950/40 p-4 text-xs text-slate-300">
                    <p className="font-medium text-slate-100">How registration works</p>
                    <ul className="mt-2 space-y-1">
                      <li>• Most churches use staff invitations as the primary way to join.</li>
                      <li>• If self-registration is enabled, your request may need approval before access is granted.</li>
                      <li>• You’ll receive an email with next steps once your registration is reviewed.</li>
                    </ul>
                  </div>
                )}

                <Button
                  type="submit"
                  className="w-full h-11 text-base font-semibold shadow-lg shadow-primary/30 transition-all duration-300 hover:shadow-xl hover:shadow-primary/40"
                  disabled={isLoading}
                >
                  {isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                  {mode === "login" && "Sign in"}
                  {mode === "signup" && "Create account"}
                  {mode === "forgot" && "Send Reset Link"}
                  {mode === "resend" && "Resend Verification Email"}
                </Button>
              </form>
            )}

            {/* Login/Signup toggle */}
            {(mode === "login" || mode === "signup") && (
              <div className="mt-6 text-center text-sm">
                <span className="text-slate-400">
                  {mode === "login" ? "Don't have an account?" : "Already have an account?"}
                </span>{" "}
                <button
                  type="button"
                  onClick={() => switchMode(mode === "login" ? "signup" : "login")}
                  className="font-semibold text-primary hover:text-primary/80 transition-colors"
                >
                  {mode === "login" ? "Sign up" : "Sign in"}
                </button>
                {mode === "login" && (
                  <p className="mt-2 text-xs text-slate-500">
                    Use an invite link from your church if self-registration isn’t enabled.
                  </p>
                )}
              </div>
            )}

            {/* Resend verification link - shown on login */}
            {mode === "login" && (
              <div className="mt-3 text-center">
                <button
                  type="button"
                  onClick={() => switchMode("resend")}
                  className="text-sm text-slate-500 hover:text-slate-300 transition-colors"
                >
                  Didn't receive a verification email?
                </button>
              </div>
            )}

            {/* Account recovery link (#4562) — for accounts stranded by the
                pre-#4561 invite signUp() bug: an unconfirmed account with no
                org membership that "Didn't receive a verification email?"
                above can't actually fix, since it re-sends via the same
                unreliable path. Carries the already-validated redirectTarget
                (e.g. /accept-invite?token=...) through to /recover-account
                and back, so recovering doesn't strand the user a second
                time outside the invite they arrived from (#4748 review). */}
            {mode === "login" && (
              <div className="mt-1 text-center">
                <button
                  type="button"
                  onClick={() => {
                    const params = new URLSearchParams();
                    if (email) params.set("email", email);
                    if (redirectTarget !== "/") params.set("redirect", redirectTarget);
                    const query = params.toString();
                    navigate(`/recover-account${query ? `?${query}` : ""}`);
                  }}
                  className="text-sm text-slate-500 hover:text-slate-300 transition-colors"
                >
                  Trouble accessing an invited account?
                </button>
              </div>
            )}

            {/* Back to sign in for forgot/resend modes */}
            {(mode === "forgot" || mode === "resend") && !resetSent && !resendSent && (
              <div className="mt-6 text-center">
                <button
                  type="button"
                  onClick={() => switchMode("login")}
                  className="text-sm text-slate-400 hover:text-white transition-colors font-medium"
                >
                  <ArrowLeft className="mr-1 h-3 w-3 inline" />
                  Back to Sign In
                </button>
              </div>
            )}
          </div>

          {/* Footer */}
          <p className="text-center text-sm text-slate-500">
            By signing in, you agree to our{" "}
            <a
              href="https://forgehq.io/terms"
              target="_blank"
              rel="noopener noreferrer"
              className="underline hover:text-slate-700 transition-colors"
            >
              terms of service
            </a>{" "}
            and{" "}
            <a
              href="https://forgehq.io/privacy"
              target="_blank"
              rel="noopener noreferrer"
              className="underline hover:text-slate-700 transition-colors"
            >
              privacy policy
            </a>
            .
          </p>
        </div>
      </div>
    </main>
  );
}
